import React, { useEffect, useState } from 'react';
import {
  Box, Button, TextField, Typography, Paper, Grid, Alert,
  FormControlLabel, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip,
} from '@mui/material';
import { Email, Send } from '@mui/icons-material';
import api from '../../api/client';

export default function SmtpPage() {
  const [form, setForm] = useState({
    smtp_host: '', smtp_port: 587, smtp_username: '', smtp_password: '',
    smtp_from: '', smtp_tls: true, notify_on_assignment: true, notify_on_completion: true,
  });
  const [testEmail, setTestEmail] = useState('');
  const [saved, setSaved] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [emailLogs, setEmailLogs] = useState<any[]>([]);

  const loadLogs = () => {
    api.get('/smtp/email-logs?limit=25').then((r) => setEmailLogs(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get('/smtp').then((r) => setForm((f) => ({ ...f, ...r.data }))).catch(() => {});
    loadLogs();
  }, []);

  const handleSave = async () => {
    setSaved(false);
    setResult(null);
    try {
      await api.put('/smtp', form);
      setSaved(true);
      if (!testEmail && form.smtp_from) setTestEmail(form.smtp_from);
    } catch { setResult({ type: 'error', message: 'Failed to save' }); }
  };

  const handleTest = async () => {
    if (!testEmail) {
      setResult({ type: 'error', message: 'Enter a recipient email address to send the test to' });
      return;
    }
    setResult(null);
    try {
      const res = await api.post('/smtp/test', { to_email: testEmail });
      setResult({ type: 'success', message: res.data.detail });
      loadLogs();
    } catch (err: any) {
      setResult({ type: 'error', message: err.response?.data?.detail || 'Test failed' });
      loadLogs();
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>SMTP / Email Configuration</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure email for notifications on call assignment and completion. SMS is sent via carrier email gateways (e.g. 5551234567@vtext.com).
      </Typography>
      {saved && <Alert severity="success" sx={{ mb: 2 }}>Configuration saved</Alert>}
      {result && <Alert severity={result.type} sx={{ mb: 2 }}>{result.message}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>SMTP Server</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="SMTP Host" value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} placeholder="smtp.gmail.com" />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField fullWidth label="Port" type="number" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: Number(e.target.value) })} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField fullWidth label="From Address" value={form.smtp_from} onChange={(e) => setForm({ ...form, smtp_from: e.target.value })} placeholder="cad@example.com" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="Username" value={form.smtp_username} onChange={(e) => setForm({ ...form, smtp_username: e.target.value })} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="Password" type="password" value={form.smtp_password} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControlLabel control={<Switch checked={form.smtp_tls} onChange={(_, c) => setForm({ ...form, smtp_tls: c })} />} label="Use TLS encryption" />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Notification Settings</Typography>
        <FormControlLabel control={<Switch checked={form.notify_on_assignment} onChange={(_, c) => setForm({ ...form, notify_on_assignment: c })} />} label="Send notifications on call assignment" />
        <FormControlLabel control={<Switch checked={form.notify_on_completion} onChange={(_, c) => setForm({ ...form, notify_on_completion: c })} />} label="Send notifications on call completion" />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Personnel must have an email and/or SMS gateway email configured in their profile to receive notifications.
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button variant="contained" startIcon={<Email />} onClick={handleSave}>Save Configuration</Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Test Email</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField label="Recipient Email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@example.com" sx={{ flex: 1 }} />
          <Button variant="outlined" startIcon={<Send />} onClick={handleTest}>Send Test</Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Email Log (Last 25)</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Recipient</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {emailLogs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>{new Date(log.timestamp).toLocaleString('en-US', { hour12: false })}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{log.recipient}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{log.subject}</TableCell>
                  <TableCell>
                    <Chip size="small" label={log.success ? 'Sent' : 'Failed'} color={log.success ? 'success' : 'error'} sx={{ fontSize: 11 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: 'error.main' }}>{log.error || '—'}</TableCell>
                </TableRow>
              ))}
              {emailLogs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 3 }}>No email log entries</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
